import {
  RateAnalysisForm,
  RateAnalysis,
  QuoteRequest,
  Quote,
} from "../types/rate-analysis";
import { calculateTotalMonthlyPayment } from "../utils/mortgageCalculations";
import { getCurrentStateAbbreviation } from "../utils/stateUtils";
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const getQuoteRequest = async (id: string): Promise<QuoteRequest> => {
  const response = await fetch(`${API_BASE_URL}/quote-requests/${id}`);

  if (!response.ok) {
    throw new Error("Failed to fetch quote request");
  }

  return response.json();
};

export const getAnalysis = async (id: string): Promise<RateAnalysis | null> => {
  const response = await fetch(`${API_BASE_URL}/quote-requests/${id}/analysis`);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error("Failed to fetch analysis");
  }

  return response.json();
};

export const submitAnalysis = async (
  formData: RateAnalysisForm
): Promise<QuoteRequest> => {
  const url = `${API_BASE_URL}/quote-requests/submit`;

  // Add state to the form data
  const dataWithState = {
    ...formData,
    state: getCurrentStateAbbreviation(),
  };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(dataWithState),
  });

  if (!response.ok) {
    throw new Error("Failed to submit analysis");
  }

  return response.json();
};

export const addQuote = async (
  requestId: string,
  quoteData: Partial<Quote>
): Promise<Quote> => {
  const response = await fetch(
    `${API_BASE_URL}/quote-requests/${requestId}/quotes`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(quoteData),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to add quote");
  }

  return response.json();
};

// Helper function to transform raw data into the expected format
export const transformAnalysisData = (
  analysis: RateAnalysis,
  quoteRequest: QuoteRequest
): RateAnalysis => {
  // Calculate loan amount
  const loanAmount =
    quoteRequest.loan_type === "purchase"
      ? (quoteRequest.purchase_price || 0) - (quoteRequest.down_payment || 0)
      : quoteRequest.remaining_balance || 0;

  // Transform broker quote if it exists
  const transformedBrokerQuote = quoteRequest.broker_rate
    ? {
        ...quoteRequest.quotes[0], // Use the first quote as a base
        rate: quoteRequest.broker_rate,
        lenderFees: quoteRequest.broker_lender_fees || 0,
        mortgageInsurance: quoteRequest.broker_mortgage_insurance || 0,
        monthlyPayment: calculateTotalMonthlyPayment(
          loanAmount,
          quoteRequest.broker_rate,
          360, // Assuming 30-year term
          quoteRequest.broker_mortgage_insurance || 0
        ),
        source: "BROKER" as const,
      }
    : undefined;

  // Transform the recommended quote if it exists
  const recommendedQuote = quoteRequest.quotes.find((q) => q.isRecommended);
  const transformedRecommendedQuote = recommendedQuote
    ? {
        ...recommendedQuote,
        monthlyPayment: calculateTotalMonthlyPayment(
          loanAmount,
          recommendedQuote.rate,
          360,
          recommendedQuote.mortgageInsurance || 0
        ),
      }
    : undefined;

  return {
    ...analysis,
    currentRate: transformedBrokerQuote,
    comparisonRate: transformedRecommendedQuote,
  };
};
